import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Download, File as FileIcon, Coins, Receipt } from 'lucide-react';
import Popup from '../../../../components/common-components/pop-up/Popup';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import footerStyles from '../../../../components/form-components/common-components/FormPopupFooter.module.css';
import { useStore } from '../../../../hooks/useStore';
import type { IExpenseResult, IExpenseFileResult } from '../../../../models/expense/ExpenseModel';
import { expenseService } from '../../../../services/modules/expense/expense.service';
import { toast } from 'react-toastify';
import styles from './ExpenseDetailPopup.module.css';

const ExpenseDetailPopup = () => {
  const { popupStore, expenseStore } = useStore();
  const [expense, setExpense] = useState<IExpenseResult | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (popupStore.isExpenseDetailOpen && expenseStore.selectedExpense) {
      void loadExpenseDetails();
    }
  }, [popupStore.isExpenseDetailOpen, expenseStore.selectedExpense]);

  const loadExpenseDetails = async () => {
    if (!expenseStore.selectedExpense?.id) {
      return;
    }

    setLoading(true);
    try {
      const expenseData = await expenseStore.getExpenseById(expenseStore.selectedExpense.id);
      if (expenseData) {
        setExpense(expenseData);
      }
    } catch (error) {
      console.error('Error loading expense details:', error);
      toast.error('Harcama detayları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFile = async (fileUniqueId: string) => {
    try {
      await expenseService.downloadExpenseFile(fileUniqueId);
      toast.success('Dosya indirildi');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Dosya indirilirken bir hata oluştu');
    }
  };

  const handleClose = () => {
    popupStore.setIsExpenseDetailOpen(false);
    expenseStore.setSelectedExpense(null);
    setExpense(null);
  };

  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
    }).format(amount);
  };

  const formatDate = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'Beklemede';
      case 'Approved':
        return 'Onaylandı';
      case 'Rejected':
        return 'Reddedildi';
      default:
        return status;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Pending':
        return styles.statusPending;
      case 'Approved':
        return styles.statusApproved;
      case 'Rejected':
        return styles.statusRejected;
      default:
        return '';
    }
  };

  if (!expenseStore.selectedExpense && !expense) {
    return null;
  }

  return (
    <Popup
      isOpen={popupStore.isExpenseDetailOpen}
      onClose={handleClose}
      title="Harcama Detayları"
      description="Harcama kaydı detaylarını görüntüleyin"
      variant="info"
      icon={<Receipt size={22} strokeWidth={2} />}
      footer={
        <div className={footerStyles.actions}>
          <CommonButton
            type="outline"
            className={footerStyles.cancelBtn}
            onClick={handleClose}
          >
            Kapat
          </CommonButton>
        </div>
      }
    >
      <div className={styles.container}>
        {loading ? (
          <div className={styles.loading}>Yükleniyor...</div>
        ) : expense ? (
          <>
            <ul className={styles.detailList}>
              <li className={styles.detailItem}>
                <span className={styles.detailLabel}>Miktar</span>
                <span className={styles.detailValue}>
                  <Coins size={16} strokeWidth={2} className={styles.currencyIcon} />
                  {formatAmount(expense.amount)}
                </span>
              </li>
              <li className={styles.detailItem}>
                <span className={styles.detailLabel}>Harcama Tarihi</span>
                <span className={styles.detailValue}>{formatDate(expense.expenseDate)}</span>
              </li>
              <li className={styles.detailItem}>
                <span className={styles.detailLabel}>Açıklama</span>
                <span className={styles.detailValue}>{expense.description}</span>
              </li>
              <li className={styles.detailItem}>
                <span className={styles.detailLabel}>Durum</span>
                <span className={`${styles.statusBadge} ${getStatusClass(expense.status)}`}>
                  {getStatusText(expense.status)}
                </span>
              </li>
              <li className={styles.detailItem}>
                <span className={styles.detailLabel}>Kullanıcı</span>
                <span className={styles.detailValue}>{expense.userName}</span>
              </li>
              <li className={styles.detailItem}>
                <span className={styles.detailLabel}>Oluşturulma Tarihi</span>
                <span className={styles.detailValue}>{formatDate(expense.createdDate)}</span>
              </li>
            </ul>

            {expense.files && expense.files.length > 0 ? (
              <div className={styles.filesSection}>
                <h3 className={styles.filesTitle}>Dosyalar</h3>
                <div className={styles.fileList}>
                  {expense.files.map((file: IExpenseFileResult) => (
                    <div key={file.id} className={styles.fileItem}>
                      <FileIcon size={16} className={styles.fileIcon} />
                      <span className={styles.fileName} title={file.fileName}>
                        {file.fileName}
                      </span>
                      <button
                        type="button"
                        className={styles.downloadButton}
                        onClick={() => void handleDownloadFile(file.uniqueId)}
                        title="İndir"
                      >
                        <Download size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className={styles.error}>Harcama bilgisi bulunamadı</div>
        )}
      </div>
    </Popup>
  );
};

export default observer(ExpenseDetailPopup);
